import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Ban, ExternalLink, Flag, Maximize2, Music2, X } from "lucide-react";
import VideoPlayer from "@/components/VideoPlayer";
import { StoryStage } from "@/components/StoryStage";
import { resolveMediaUrl } from "@/components/FileUpload";
import { imageVariant } from "@/lib/mediaUrl";

// Libellés FR des catégories de signalement (le `reason` stocké est dans la langue du signaleur).
export const REPORT_CATEGORY_LABELS = {
  spam: "Spam ou arnaque",
  hate: "Harcèlement ou discours haineux",
  sexual: "Nudité ou contenu sexuel",
  violence: "Violence ou contenu choquant",
  misinfo: "Fausse information",
  other: "Autre contenu inapproprié",
  child_safety: "Sécurité des enfants (CSAE)",
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

const Avatar = ({ user, cls = "h-7 w-7" }) => (user?.avatar_url
  ? <img src={imageVariant(user.avatar_url, 32)} alt="" className={`${cls} rounded-full object-cover shrink-0`} />
  : <div className={`${cls} rounded-full grid place-items-center text-[11px] font-bold text-white shrink-0`} style={{ background: "var(--yalola-primary)" }}>{(user?.name || "?")[0]}</div>);

// Carte d'un blocage entre membres : ce n'est PAS un contenu — un membre a bloqué un profil (action personnelle).
// La file ne la remonte que pour repérer les profils bloqués par plusieurs personnes.
export const ModerationBlockBanner = ({ item: it }) => {
  const blocker = it.reporter?.name || "Un membre";
  const blocked = it.target_user?.name || it.target_user?.email || "ce profil";
  const n = it.blocks_count || 0;
  return (
    <div className="mt-2 rounded-xl p-3 text-xs space-y-1.5" style={{ background: "var(--yalola-surface-alt)", borderLeft: "3px solid var(--yalola-primary)" }} data-testid={`mod-block-banner-${it.id}`}>
      <div className="font-bold flex items-center gap-1.5"><Ban size={13} style={{ color: "var(--yalola-primary)" }} /> Blocage entre membres</div>
      <div><b>{blocker}</b> a bloqué <b>{blocked}</b>{it.block_active === false && <span className="ml-1 font-semibold" style={{ color: "#B45309" }}>· blocage levé depuis</span>}.</div>
      <div style={{ color: "var(--yalola-text-muted)" }}>Action personnelle : ils ne se voient plus l'un l'autre. <b>Aucun contenu n'est concerné</b>, rien n'est caché au reste de la communauté.</div>
      {it.block_reason && <div data-testid={`mod-block-reason-${it.id}`}><b>Raison donnée par {blocker} :</b> « {it.block_reason} »</div>}
      <div className="font-semibold" data-testid={`mod-blocks-count-${it.id}`} style={{ color: n >= 3 ? "#B91C1C" : n >= 2 ? "#B45309" : "var(--yalola-text)" }}>
        {n <= 1 ? "Seul ce membre a bloqué ce profil pour l'instant." : `${n} membres différents ont bloqué ce profil.`}
      </div>
    </div>
  );
};

// Motif : libellé FR de la catégorie + texte d'origine s'il diffère (langue du signaleur).
export const ModerationReason = ({ item: it }) => {
  const label = REPORT_CATEGORY_LABELS[it.category];
  const raw = (it.reason || "").trim();
  if (!label && !raw) return null;
  return (
    <div className="mt-1 text-xs" data-testid={`mod-reason-${it.id}`}>
      <b>Motif du signalement :</b> {label || raw}
      {label && raw && raw !== label && <span className="ml-1" style={{ color: "var(--yalola-text-muted)" }}>· « {raw} »</span>}
    </div>
  );
};

// Qui a signalé (lien profil) + nombre de membres distincts ayant signalé la même cible.
export const ModerationReporter = ({ item: it }) => {
  if (!it.reporter) return null;
  const isBlock = it.verdict?.reason === "user_block";
  const n = it.reports_count || 0;
  return (
    <div className="mt-1.5 flex items-center gap-2 text-xs" data-testid={`mod-reporter-${it.id}`}>
      <Flag size={12} style={{ color: "var(--yalola-primary)" }} />
      <span style={{ color: "var(--yalola-text-muted)" }}>{isBlock ? "Bloqué par" : "Signalé par"}</span>
      <a href={`/profile/${it.reporter.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-semibold hover:underline" data-testid={`mod-reporter-link-${it.id}`}>
        <Avatar user={it.reporter} cls="h-5 w-5" />{it.reporter.name || "Membre supprimé"}
      </a>
      {n > 1 && <span className="chip text-[10px]" data-testid={`mod-reports-count-${it.id}`}>{n} signalements</span>}
    </div>
  );
};

const StoryStateBadge = ({ it }) => {
  const [txt, color] = it.target_deleted ? ["Supprimée", "#B91C1C"] : it.target_is_hidden ? ["Déjà masquée", "#B91C1C"] : it.target_expired ? [`Expirée le ${fmtDate(it.target_expires_at)}`, "#B45309"] : ["Active", "#15803D"];
  return <span className="ml-auto text-[10px] font-bold uppercase tracking-wide" style={{ color }} data-testid={`mod-story-state-${it.id}`}>{txt}</span>;
};

// Lot 602 — état lisible en une phrase : où en est la story et ce que l'aperçu représente.
const storyStateLine = (it) => {
  if (it.target_deleted) return it.target_from_snapshot ? "Story supprimée par l'auteur (ou expirée et purgée) — l'aperçu est la capture prise au moment du signalement." : "Story supprimée avant toute capture : contenu indisponible.";
  if (it.target_is_hidden) return `Story masquée (retirée du profil) — encore visible ici et dans l'app pour les modérateurs${it.target_expires_at ? ` jusqu'au ${fmtDate(it.target_expires_at)}` : ""}.`;
  if (it.target_expired) return `Story expirée le ${fmtDate(it.target_expires_at)} — plus visible dans l'app ; l'aperçu ci-dessus est fidèle au contenu publié.`;
  return `Story active${it.target_expires_at ? ` jusqu'au ${fmtDate(it.target_expires_at)}` : ""} — visible dans l'app telle qu'affichée ci-dessus.`;
};

// Aperçu d'une story signalée : auteur, état, rendu FIDÈLE (StoryStage : média + texte + stickers + dessin + lien,
// cadre 9:16 comme dans l'app), lien d'ouverture directe tant qu'elle n'est pas expirée (masquée → modérateurs).
export const ModerationStoryPreview = ({ item: it }) => {
  const type = it.target_media_type || "text";
  const src = it.target_hls_url || it.target_media_url;
  const story = it.target_story && (it.target_story.media_type || it.target_story.text) ? it.target_story : null;
  const canOpen = !it.target_deleted && !it.target_expired && it.target_author_id;
  const [full, setFull] = useState(false);
  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);
  return (
    <div className="mt-1.5 rounded-xl p-2 space-y-2 text-xs" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`mod-story-${it.id}`}>
      <div className="flex items-center gap-2">
        {it.target_user
          ? <a href={`/profile/${it.target_user.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-semibold hover:underline" data-testid={`mod-story-author-${it.id}`}><Avatar user={it.target_user} />{it.target_user.name}</a>
          : <span style={{ color: "var(--yalola-text-muted)" }}>Auteur inconnu</span>}
        <StoryStateBadge it={it} />
      </div>
      {story ? (
        <div className="relative mx-auto w-full max-w-[250px] aspect-[9/16] rounded-xl overflow-hidden bg-black" data-testid={`mod-story-stage-${it.id}`} data-media-type={type}>
          <StoryStage story={story} muted safeTop="0.75rem" safeBottom="0.75rem" testIdPrefix={`mod-stage-${it.id}`} />
          <button type="button" onClick={() => setFull(true)} className="absolute bottom-2 right-2 z-20 h-8 w-8 grid place-items-center rounded-full text-white" style={{ background: "rgba(0,0,0,0.55)" }} title="Agrandir" aria-label="Agrandir" data-testid={`mod-story-expand-${it.id}`}><Maximize2 size={14} /></button>
        </div>
      ) : (
        <>
          {type === "video" && src && <VideoPlayer mode="controls" src={resolveMediaUrl(src)} poster={it.target_thumb ? resolveMediaUrl(it.target_thumb) : undefined} className="w-full rounded-lg overflow-hidden bg-black" maxHeight="18rem" testId={`mod-story-video-${it.id}`} />}
          {type === "image" && it.target_media_url && <img src={resolveMediaUrl(it.target_media_url)} alt="" className="w-full max-h-72 rounded-lg object-contain bg-black" data-testid={`mod-story-image-${it.id}`} />}
          {type === "text" && (
            <div className="rounded-lg px-4 py-6 text-center text-white text-sm font-semibold whitespace-pre-wrap break-words" style={{ background: it.target_bg || "#111827", minHeight: 96 }} data-testid={`mod-story-text-${it.id}`}>
              {it.target_text || <span className="opacity-60 font-normal">(story texte sans texte)</span>}
            </div>
          )}
        </>
      )}
      {type !== "text" && it.target_text && <div className="italic" data-testid={`mod-story-caption-${it.id}`}>« {it.target_text} »</div>}
      {story?.music_url && <div className="flex items-center gap-1.5" data-testid={`mod-story-music-${it.id}`}><Music2 size={12} /> Musique : <b>{story.music_title || "piste audio"}</b> <a href={resolveMediaUrl(story.music_url)} target="_blank" rel="noreferrer" className="underline">écouter</a></div>}
      {type !== "text" && !src && <div style={{ color: "var(--yalola-text-muted)" }}>⚠️ Média indisponible : story supprimée avant la capture automatique.</div>}
      <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid={`mod-story-state-line-${it.id}`}>{storyStateLine(it)}</div>
      {canOpen && (
        <a href={`/s/story/${it.target_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium" style={{ color: "var(--yalola-primary)" }} data-testid={`mod-story-open-${it.id}`} data-hidden={it.target_is_hidden ? "1" : undefined}>
          <ExternalLink size={12} /> {it.target_is_hidden ? "Voir la story masquée dans l'app (modérateurs) →" : "Voir la story dans l'app →"}
        </a>
      )}
      {full && story && createPortal(
        <div className="fixed inset-0 z-[120] grid place-items-center p-3" style={{ background: "rgba(0,0,0,0.92)" }} onClick={() => setFull(false)} data-testid={`mod-story-full-${it.id}`}>
          <div className="relative max-h-full max-w-full rounded-2xl overflow-hidden bg-black" style={{ height: "min(94vh, calc(100vw * 16 / 9))", aspectRatio: "9 / 16" }} onClick={(e) => e.stopPropagation()}>
            <StoryStage story={story} muted={false} safeTop="1rem" safeBottom="1rem" testIdPrefix={`mod-stage-full-${it.id}`} />
            <button type="button" onClick={() => setFull(false)} className="absolute top-3 right-3 z-20 h-9 w-9 grid place-items-center rounded-full text-white" style={{ background: "rgba(0,0,0,0.6)" }} aria-label="Fermer" data-testid={`mod-story-full-close-${it.id}`}><X size={18} /></button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
